import { type ClinicFlags, type DashboardNamesType, dashboardTabsData, type FeatureFlags } from '../Types';

export const ANALYTICS_PATH_PREFIX = '/analytics';

interface ReportRouteDefinition {
	readonly path: string;
	readonly dashboardName: DashboardNamesType;
}

const engagementHubProviderPath = 'engagement-hub/provider-engagement';
const engagementHubQualityPath = 'engagement-hub/quality-engagement';

const reportRouteDefinitions: readonly ReportRouteDefinition[] = [
	{ path: engagementHubProviderPath, dashboardName: 'engagement_hub' },
	{ path: engagementHubQualityPath, dashboardName: 'care_team_engagement' },
	{ path: 'patients', dashboardName: 'patient_risk_gaps' },
	{ path: 'risk-adjustment', dashboardName: 'risk_adjustment' },
	{ path: 'quality/overview', dashboardName: 'quality_overview' },
	{ path: 'quality/measure-performance', dashboardName: 'quality_measures_breakdown' },
	{ path: 'quality/provider-performance', dashboardName: 'quality_provider' },
	{ path: 'quality/payer-performance', dashboardName: 'quality_payers' },
	{ path: 'quality/quality-patients', dashboardName: 'patient_level_report_quality' },
	{ path: 'actions-reports/provider-actions', dashboardName: 'detailed_actions_report_provider' },
	{ path: 'actions-reports/coder-actions', dashboardName: 'detailed_actions_report_coder' },
	{ path: 'actions-reports/actions-on-patients', dashboardName: 'patient_level_report_risk' },
	{ path: 'last-year-reports/provider-actions', dashboardName: 'detailed_actions_report_provider_last_year' },
	{ path: 'last-year-reports/coder-actions', dashboardName: 'detailed_actions_report_coder_last_year' },
	{ path: 'last-year-reports/patients', dashboardName: 'patient_risk_gaps_last_year' },
] satisfies readonly ReportRouteDefinition[];

// Hand-rolled scan instead of `value.replace(/^\/+|\/+$/g, '')` — Sonar flags
// the `+` quantifier on `/` as a ReDoS risk, so please keep the regex away.
function trimSlashes(value: string): string {
	let startIndex = 0;
	let endIndex = value.length;
	while (startIndex < endIndex && value[startIndex] === '/') {
		startIndex++;
	}
	while (endIndex > startIndex && value[endIndex - 1] === '/') {
		endIndex--;
	}
	return value.slice(startIndex, endIndex);
}

export function getRelativePathFromPathname(pathname: string): string {
	const trimmed = trimSlashes(pathname);
	const analyticsSegment = ANALYTICS_PATH_PREFIX.slice(1);
	if (trimmed === '' || trimmed === analyticsSegment) {
		return '';
	}
	const analyticsSegmentWithSlash = `${analyticsSegment}/`;
	if (!trimmed.startsWith(analyticsSegmentWithSlash)) {
		return '';
	}
	return trimmed.slice(analyticsSegmentWithSlash.length);
}

export function parseReportPathToDashboardName(relativePath: string): DashboardNamesType | null {
	const trimmed = trimSlashes(relativePath);
	if (trimmed === '') {
		return null;
	}
	return reportRouteDefinitions.find((d) => d.path === trimmed)?.dashboardName ?? null;
}

interface NavigationLeaf {
	readonly dashboardName: DashboardNamesType;
}

function collectNavigationLeaves(permissions: ClinicFlags, featureFlags: FeatureFlags): readonly NavigationLeaf[] {
	return dashboardTabsData.flatMap((tab): readonly NavigationLeaf[] => {
		const isFeatureEnabled = !tab.featureFlag || featureFlags[tab.featureFlag];
		const hasRequiredPermissions = tab.requiredPermissions.every((permission) => permissions[permission]);
		const isExcludedByPermission = tab.excludedByPermissions?.some((permission) => permissions[permission]);
		if (!isFeatureEnabled || !hasRequiredPermissions || isExcludedByPermission) {
			return [];
		}
		if (tab.children) {
			return tab.children
				.filter((innerTab) => !innerTab.featureFlag || featureFlags[innerTab.featureFlag])
				.map((innerTab) => ({ dashboardName: innerTab.dashboardName }));
		}
		return [{ dashboardName: tab.dashboardName }];
	});
}

function findReportPathForDashboardName(dashboardName: DashboardNamesType): string | null {
	return reportRouteDefinitions.find((d) => d.dashboardName === dashboardName)?.path ?? null;
}

export function getDefaultDashboardUrlPath(permissions: ClinicFlags, featureFlags: FeatureFlags): string {
	const firstLeaf = collectNavigationLeaves(permissions, featureFlags)[0];
	const path = firstLeaf === undefined ? null : findReportPathForDashboardName(firstLeaf.dashboardName);
	return path ?? engagementHubProviderPath;
}

export function getFirstPermittedDashboardName(
	permissions: ClinicFlags,
	featureFlags: FeatureFlags,
): DashboardNamesType {
	return collectNavigationLeaves(permissions, featureFlags)[0]?.dashboardName ?? 'engagement_hub';
}

export function findDashboardUrlPathInMenu(
	dashboardName: DashboardNamesType,
	permissions: ClinicFlags,
	featureFlags: FeatureFlags,
): string | null {
	const isPermitted = collectNavigationLeaves(permissions, featureFlags).some(
		(leaf) => leaf.dashboardName === dashboardName,
	);
	return isPermitted ? findReportPathForDashboardName(dashboardName) : null;
}

export function isDashboardPermittedForUser(
	dashboardName: DashboardNamesType,
	permissions: ClinicFlags,
	featureFlags: FeatureFlags,
): boolean {
	return collectNavigationLeaves(permissions, featureFlags).some((l) => l.dashboardName === dashboardName);
}
